import type { JSX } from 'react';

/**
 * Реестр одиночных UI-глифов (icon_handoff_spec §G · 16, моно-штрих, 16-20px).
 * Замок/празднование/warning/синк/auth/capstone/senior-tip и т.п. — точечная
 * замена эмодзи в UI-шроме. Ключи — по смыслу.
 *
 * Как SkillIcon/BadgeIcon: реестр = внутреннее содержимое, обёртка `<UiGlyph>`
 * задаёт штрих `var(--ink)` 1.7 round (тема-зависимо). Hex спеки → палитра-vars.
 * Нет ключа → null, вызывающий рендерит эмодзи-фолбэк.
 */
const ICONS: Record<string, JSX.Element> = {
  gift: (
    <>
      <rect x="4" y="9" width="16" height="4" rx="1" />
      <path d="M5.5 13 V19 C5.5 20.1 6.4 21 7.5 21 H16.5 C17.6 21 18.5 20.1 18.5 19 V13" />
      <path d="M12 9 V21" />
      <path d="M12 9 C9.5 9 7.8 7.8 7.8 6.2 C7.8 4.9 8.8 4 10 4 C11.5 4 12 6 12 9 Z M12 9 C14.5 9 16.2 7.8 16.2 6.2 C16.2 4.9 15.2 4 14 4 C12.5 4 12 6 12 9 Z" stroke="var(--accent)" />
    </>
  ),
  lock: (
    <>
      <rect x="5.5" y="10.5" width="13" height="9.5" rx="2" />
      <path d="M8.5 10.5 V8 C8.5 6.1 10.1 4.5 12 4.5 S15.5 6.1 15.5 8 V10.5" />
      <circle cx="12" cy="15.2" r="1.3" fill="var(--ink)" stroke="none" />
    </>
  ),
  unlock: (
    <>
      <rect x="5.5" y="10.5" width="13" height="9.5" rx="2" />
      <path d="M8.5 10.5 V8 C8.5 6.1 10.1 4.5 12 4.5 C13.6 4.5 14.9 5.6 15.3 7" stroke="var(--green)" />
      <circle cx="12" cy="15.2" r="1.3" fill="var(--green)" stroke="none" />
    </>
  ),
  celebration: (
    <>
      <path d="M4 20 L7.5 10.5 L13.5 16.5 Z" />
      <path d="M13 9 C14.5 7.5 16.5 7.8 18 6 M14.8 12.2 C16.8 11.8 17.8 12.8 20 12.4" strokeWidth={1.4} />
      <circle cx="15" cy="4.8" r="1" fill="var(--accent)" stroke="none" />
      <circle cx="19.5" cy="9" r="1" fill="var(--teal)" stroke="none" />
      <circle cx="20" cy="16.5" r="1" fill="var(--mustard)" stroke="none" />
    </>
  ),
  sparkles: (
    <>
      <path d="M12 4 l1.6 4.4 4.4 1.6 -4.4 1.6 -1.6 4.4 -1.6 -4.4 -4.4 -1.6 4.4 -1.6 Z" />
      <path d="M18.5 14.5 l.9 2.1 2.1 .9 -2.1 .9 -.9 2.1 -.9 -2.1 -2.1 -.9 2.1 -.9 Z" stroke="var(--mustard)" />
      <circle cx="6" cy="18" r="1" fill="var(--mustard)" stroke="none" />
    </>
  ),
  'flag-filled': (
    <>
      <path d="M6 21 V4" />
      <path d="M6 4.5 H16.5 l-2 2.9 2 2.9 H6 Z" fill="var(--accent-deep)" stroke="var(--accent-deep)" />
    </>
  ),
  'flag-empty': (
    <>
      <path d="M6 21 V4" />
      <path d="M6 4.5 H16.5 l-2 2.9 2 2.9 H6" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="15" rx="2" />
      <path d="M4 10 H20" />
      <path d="M8 3.5 V7 M16 3.5 V7" />
      <rect x="7.5" y="13" width="3.4" height="3.4" rx="0.8" fill="var(--accent)" stroke="none" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4.5 C12.5 4.5 12.9 4.8 13.2 5.4 L20.4 18.2 C21 19.3 20.4 20.3 19.2 20.3 H4.8 C3.6 20.3 3 19.3 3.6 18.2 L10.8 5.4 C11.1 4.8 11.5 4.5 12 4.5 Z" />
      <path d="M12 9.5 V14" stroke="var(--accent-deep)" strokeWidth={1.9} />
      <circle cx="12" cy="17" r="1.1" fill="var(--accent-deep)" stroke="none" />
    </>
  ),
  sync: (
    <>
      <path d="M12 21 V11" />
      <circle cx="12" cy="9.5" r="1.4" fill="var(--teal)" stroke="var(--teal)" />
      <path d="M8.5 6 C10.4 4.2 13.6 4.2 15.5 6 M6.2 3.6 C9.4 .9 14.6 .9 17.8 3.6" transform="translate(0,3)" stroke="var(--teal)" />
      <path d="M8 21 H16" />
    </>
  ),
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="M4.5 7 L12 13 L19.5 7" stroke="var(--teal)" />
    </>
  ),
  timer: (
    <>
      <circle cx="12" cy="13.5" r="7" />
      <path d="M10 3.5 H14 M12 3.5 V6.5" />
      <path d="M12 13.5 L12 9.5" stroke="var(--accent-deep)" strokeWidth={1.9} />
      <path d="M12 13.5 L14.6 15" stroke="var(--accent-deep)" strokeWidth={1.9} />
    </>
  ),
  chat: (
    <>
      <path d="M4 7.5 C4 6.1 5.1 5 6.5 5 H17.5 C18.9 5 20 6.1 20 7.5 V14 C20 15.4 18.9 16.5 17.5 16.5 H10 L5.5 20.2 V16.5 H6.5 C5.1 16.5 4 15.4 4 14 Z" />
      <path d="M8.5 10.8 H15.5" stroke="var(--teal)" strokeWidth={1.5} />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.5 L19 6 V11 C19 15.8 15 18.8 12 20.7 C9 18.8 5 15.8 5 11 V6 Z" />
      <path d="M9 11.4 l2 2 4 -4.3" stroke="var(--green)" strokeWidth={1.9} />
    </>
  ),
  rocket: (
    <>
      <path d="M12 3 C15 5 16.2 9 14.6 14 H9.4 C7.8 9 9 5 12 3 Z" />
      <circle cx="12" cy="8.6" r="1.7" />
      <path d="M9.4 12.2 L6.8 15 L9 15.8 M14.6 12.2 L17.2 15 L15 15.8" />
      <path d="M10.6 16.7 C10.6 18.7 11 20.2 12 21.6 C13 20.2 13.4 18.7 13.4 16.7" stroke="var(--accent)" />
    </>
  ),
  lightbulb: (
    <>
      <path d="M12 3.4 C15.4 3.3 17.9 5.8 17.8 9 C17.7 11.2 16.5 12.5 15.5 13.8 C14.9 14.6 14.6 15.3 14.5 16.2 H9.6 C9.5 15.4 9.1 14.6 8.5 13.8 C7.5 12.5 6.3 11.3 6.2 9.1 C6.1 5.9 8.6 3.5 12 3.4 Z" />
      <path d="M9.8 18.5 H14.3 M10.3 20.7 H13.8" />
      <path d="M10.5 8 C10.9 7.2 11.7 6.7 12.6 6.8" stroke="var(--mustard)" />
    </>
  ),
};

/** Есть ли в реестре UI-глиф под этот ключ. */
export function hasUiGlyph(key?: string): boolean {
  return !!key && key in ICONS;
}

/** UI-глиф по ключу; null, если ключа нет (вызывающий рендерит эмодзи). */
export function UiGlyph({ name, size = 18 }: { name: string; size?: number }) {
  const body = ICONS[name];
  if (!body) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="var(--ink)"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      {body}
    </svg>
  );
}
